const DEFAULT_PRODUCTION_API_URL = 'https://vibrion-backend.onrender.com'

function normalizeHostname(hostname: string | null | undefined): string {
  return (hostname || '').trim().toLowerCase().replace(/^\[(.*)\]$/, '$1')
}

export function isLoopbackHost(hostname: string | null | undefined): boolean {
  const normalized = normalizeHostname(hostname)
  return normalized === 'localhost' || normalized === '127.0.0.1' || normalized === '::1'
}

export function isPrivateNetworkHost(hostname: string | null | undefined): boolean {
  const normalized = normalizeHostname(hostname)
  if (!normalized) return false
  if (isLoopbackHost(normalized)) return true
  if (normalized.startsWith('10.')) return true
  if (normalized.startsWith('192.168.')) return true
  if (/^172\.(1[6-9]|2\d|3[01])\./.test(normalized)) return true
  return false
}

function isLocalWebHost(hostname: string | null | undefined): boolean {
  return isPrivateNetworkHost(hostname)
}

function getDefaultLocalApiUrl(hostname: string | null | undefined): string {
  const normalized = normalizeHostname(hostname)
  if (!normalized || normalized === 'localhost') {
    return 'http://localhost:8000'
  }
  if (normalized === '::1') {
    return 'http://[::1]:8000'
  }
  return `http://${normalized}:8000`
}

function trimTrailingSlash(url: string): string {
  return url.replace(/\/+$/, '')
}

function getConfiguredHostname(url: string): string | null {
  try {
    return new URL(url).hostname
  } catch {
    return null
  }
}

export function resolveApiBaseUrl({
  configuredUrl = process.env.REACT_APP_API_URL ?? null,
  windowHostname = typeof window !== 'undefined' ? window.location.hostname : null,
}: {
  configuredUrl?: string | null
  windowHostname?: string | null
} = {}): string {
  const pageHost = normalizeHostname(windowHostname)
  const normalizedConfiguredUrl = configuredUrl ? trimTrailingSlash(configuredUrl.trim()) : null

  if (normalizedConfiguredUrl) {
    const configuredHostname = getConfiguredHostname(normalizedConfiguredUrl)
    if (configuredHostname && isPrivateNetworkHost(configuredHostname) && !isLocalWebHost(pageHost)) {
      return DEFAULT_PRODUCTION_API_URL
    }
    return normalizedConfiguredUrl
  }

  if (isLocalWebHost(pageHost)) {
    return getDefaultLocalApiUrl(pageHost)
  }

  return DEFAULT_PRODUCTION_API_URL
}

export const API_BASE_URL = resolveApiBaseUrl()
export { DEFAULT_PRODUCTION_API_URL }
